/**
 * 24 Oct 2026 (D-1023-READING, Eli's picks) — the office answers a reading volunteer's note: a short box, one press.
 * The press queues one email to that volunteer through the Outbox (the cancel window applies); nothing is sent by itself.
 * The note stays under Needs your attention until someone presses "Got it".
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useAnnounce } from '@/components/Announcer';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { TextArea } from '@/features/people/fields';
import { postJson, useVolunteerRefresh } from './api';

export interface NoteRef {
  appointmentId: string;
  name: string;
  note: string;
}

export function ReadingNoteReplySheet({ note, onClose }: { note: NoteRef; onClose: () => void }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const uid = useId();
  const [message, setMessage] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    if (!message.trim()) {
      const m = t('reading1024.needWords');
      setErr(m);
      announce(m, 'assertive');
      return;
    }
    setErr(null);
    try {
      await postJson(`/api/volunteers/visits/${encodeURIComponent(note.appointmentId)}`, { what: 'note-reply', reply: { message: message.trim() } });
      const m = t('reading1024.queued', { name: note.name });
      toast(m);
      announce(m);
      refresh();
      onClose();
    } catch (e) {
      const m = errorText(e);
      setErr(m);
      announce(m, 'assertive');
    }
  };
  return (
    <SheetContent
      title={t('reading1024.replyTitle', { name: note.name })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('reading1024.cancel')}
          </Button>
          <Button onClick={send} data-testid="reading-reply-send">
            {t('reading1024.send')}
          </Button>
        </>
      }
    >
      <div data-testid="reading-reply-form">
        <p className="help">{t('reading1024.theirNote')}</p>
        <blockquote lang="und" data-testid="reading-reply-note">
          {note.note}
        </blockquote>
        <TextArea id={`${uid}-msg`} label={t('reading1024.yourWords')} required value={message} onChange={setMessage} help={t('reading1024.help')} />
        {err ? (
          <p role="alert" className="help" data-testid="reading-reply-err">
            {err}
          </p>
        ) : null}
      </div>
    </SheetContent>
  );
}
